import React, { useEffect, useState } from "react";
import { Animated } from "react-animated-css";
import { ButtonToolbar, DropdownButton, Dropdown } from "react-bootstrap";
import DataTable from "react-data-table-component";
import ReactPaginate from "react-paginate";
import { debounce } from "lodash";
import * as Api from "../../../General/api";
import * as Common from "../../../General/common";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls/Card";
import ModalLoading from "../../workqueue/Modal/ModalLoading";
import ProviderDashboard from "./providerDashboard";
import PopupModal from "../../modal/popupmodal";

function ProvidersList() {
  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("ProviderName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  var providerDashboardModal = null;

  useEffect(() => {
    fetchProviders();
  }, [currentPage, rowsPerPage, sortColumn, sortDirection]);

  const fetchProviders = (search = searchTerm) => {
    setIsLoading(true);
    const qry =
      `?search=${encodeURIComponent(search || "")}` +
      `&sortColumn=${sortColumn}` +
      `&sortDirection=${sortDirection}` +
      `&pageIndex=${currentPage}` +
      `&pageSize=${rowsPerPage}`;
    Api.getRequestQry(Api.Uri_CmnProviderList, qry)
      .then((res) => {
        if (res.data.statusCode === 200 && res.data.result) {
          setTotalRows(res.data.result.totalRows || 0);
          setItems(res.data.result.items || []);
        } else {
          setItems([]);
          setTotalRows(0);
        }
      })
      .catch((ex) => Common.LogError(ex, "ProvidersList", "fetchProviders"))
      .finally(() => setIsLoading(false));
  };

  const handleFilter = debounce((val) => {
    setCurrentPage(1);
    fetchProviders(val);
  }, 800);

  const handleSort = (column, direction) => {
    setSortColumn(column.sortColumn || "ProviderName");
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handlePagination = (page) => setCurrentPage(page.selected + 1);

  const handlePerPage = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const openProviderDashboard = (provider) => {
    setSelectedProvider(provider);
    setTimeout(() => {
      if (providerDashboardModal) providerDashboardModal.showModal();
    }, 100);
  };

  const formatDollar = (amount) => {
    if (amount == null) return "--";
    return `$${Number(amount).toFixed(2)}`;
  };

  const columns = [
    {
      name: "Provider Name",
      sortable: true,
      sortColumn: "ProviderName",
      minWidth: "200px",
      selector: (row) => row.providerName,
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-left"
          onClick={() => openProviderDashboard(row)}
        >
          {row.providerName}
        </button>
      ),
    },
    {
      name: "NPI Number",
      sortable: true,
      sortColumn: "NpiNumber",
      minWidth: "140px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.npiNumber, "--"),
    },
    {
      name: "Phone",
      sortable: false,
      minWidth: "130px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.phone, "--"),
    },
    {
      name: "Fax",
      sortable: false,
      minWidth: "130px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.fax, "--"),
    },
    {
      name: "Email",
      sortable: false,
      minWidth: "180px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.email, "--"),
    },
    {
      name: "Facility Count",
      sortable: true,
      sortColumn: "FacilityCount",
      minWidth: "130px",
      selector: (row) => row.facilityCount,
    },
    {
      name: "Pending CMN",
      sortable: true,
      sortColumn: "PendingCmnCount",
      minWidth: "130px",
      selector: (row) => row.pendingCmnCount,
      cell: (row) => (row.pendingCmnCount != null ? row.pendingCmnCount : 0),
    },
    {
      name: "Dollar Amount",
      sortable: true,
      sortColumn: "PendingDollarAmount",
      minWidth: "130px",
      selector: (row) => row.pendingDollarAmount,
      cell: (row) => (
        <span>{formatDollar(row.pendingDollarAmount)}</span>
      ),
    },
    {
      name: "Action",
      allowOverflow: true,
      minWidth: "100px",
      cell: (row) => (
        <ButtonToolbar className="justify-content-center">
          <div className="mr-md-2 mr-0">
            {[DropdownButton].map((DropdownType, idx) => (
              <DropdownType
                size="xs"
                variant="primary"
                title="Action"
                id={`cmn-provider-action-${idx}-${row.physicianId}`}
                key={idx}
              >
                <Dropdown.Item onClick={() => openProviderDashboard(row)}>
                  View Dashboard
                </Dropdown.Item>
              </DropdownType>
            ))}
          </div>
        </ButtonToolbar>
      ),
    },
  ];

  const CustomPagination = () => {
    const count = Math.ceil(totalRows / rowsPerPage) || 1;
    return (
      <ReactPaginate
        previousLabel=""
        nextLabel=""
        pageCount={count}
        activeClassName="active"
        forcePage={currentPage !== 0 ? currentPage - 1 : 0}
        onPageChange={handlePagination}
        pageClassName="page-item"
        nextLinkClassName="page-link"
        nextClassName="page-item next"
        previousClassName="page-item prev"
        previousLinkClassName="page-link"
        pageLinkClassName="page-link"
        containerClassName="pagination react-paginate justify-content-end my-2 pe-1"
      />
    );
  };

  return (
    <>
      <Animated animationIn="fadeIn" animationInDuration={500}>
        <Card className="example example-compact">
          <CardHeader title="All Providers" />
          <CardBody>
            <div className="react-dataTable">
              <DataTable
                noHeader
                striped
                highlightOnHover
                responsive
                pagination
                paginationServer
                sortServer
                subHeader
                columns={columns}
                data={items}
                onSort={handleSort}
                progressPending={isLoading}
                progressComponent={<ModalLoading />}
                paginationComponent={CustomPagination}
                className="react-dataTable"
                subHeaderComponent={
                  <div className="w-100 mb-2">
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: "1rem" }}>
                      <div className="d-flex align-items-center">
                        <label className="mb-0 mr-2" htmlFor="prov-search">
                          Search by provider:
                        </label>
                        <input
                          id="prov-search"
                          type="text"
                          className="form-control form-control-sm"
                          style={{ width: "220px" }}
                          placeholder="Provider name or NPI..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            handleFilter(e.target.value);
                          }}
                        />
                      </div>
                      <div className="d-flex align-items-center">
                        <label className="mb-0 mr-2" htmlFor="prov-per-page">
                          Show
                        </label>
                        <select
                          id="prov-per-page"
                          className="form-control form-control-sm"
                          style={{ width: "80px" }}
                          value={rowsPerPage}
                          onChange={handlePerPage}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span className="ml-2 text-muted">entries</span>
                      </div>
                    </div>
                  </div>
                }
              />
            </div>
          </CardBody>
        </Card>
      </Animated>

      {/* Provider Dashboard PopupModal */}
      <PopupModal
        size="xl"
        dialogClassName="modal-90w"
        title={
          selectedProvider
            ? `Provider: ${selectedProvider.providerName}`
            : "Provider Dashboard"
        }
        showButton={false}
        ref={(ref) => {
          providerDashboardModal = ref;
        }}
      >
        {selectedProvider && (
          <ProviderDashboard
            physicianId={selectedProvider.physicianId}
            providerName={selectedProvider.providerName}
          />
        )}
      </PopupModal>
    </>
  );
}

export default ProvidersList;
